import { HttpBackend, HttpHeaders, HttpParams, HttpRequest } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import { NGXLoggerServerService, INGXLoggerMetadata, NgxLoggerLevel, INGXLoggerConfig } from "ngx-logger";
import { catchError, of } from "rxjs";
import * as moment from 'moment';
import { UtilsService } from "./utils.service";

@Injectable()
export class LoggerServerOTELService extends NGXLoggerServerService {
    
    constructor(
        override httpBackend: HttpBackend, 
        override ngZone: NgZone,

        private _utils: UtilsService,
    ) {
        super(httpBackend, ngZone);
    }
    
    public override customiseRequestBody(metadata: INGXLoggerMetadata): any {
        const { message, additional, level, timestamp, fileName, lineNumber, columnNumber } = metadata;
       
        return {
            host: this._utils.otelTraceId,
            time: moment(timestamp).valueOf(), // convert to nanoseconds

            fields: {
                message: message,
            },

            event: {
                message: message,
                severity: this.severityTextMapping(level),

                attributes: {
                    file: fileName || "app.component.ts",                // Provide default if not available
                    line: lineNumber || 1,                               // Provide default if not available
                    column: columnNumber || 1                            // Provide default if not available
                },

                additional: additional
            }
        }
    }

    private severityTextMapping(level: NgxLoggerLevel) {
        switch (level) {
            case NgxLoggerLevel.TRACE:
                return "TRACE";
            case NgxLoggerLevel.DEBUG:
                return "DEBUG";
            case NgxLoggerLevel.INFO:
                return "INFO";
            case NgxLoggerLevel.LOG:
                return "LOG";
            case NgxLoggerLevel.WARN:
                return "WARN";
            case NgxLoggerLevel.ERROR:
                return "ERROR";
            case NgxLoggerLevel.FATAL:
                return "FATAL";
            default:
                return "INFO";
        }
    }

    protected override alterHttpRequest(httpRequest: HttpRequest<any>): HttpRequest<any> {
        httpRequest = httpRequest.clone({
        setHeaders: {
            ['Authorization']: `Splunk 25f28616-2c12-428f-ad46-b327648ea042`
        },
        });

        return httpRequest;
    }

    protected override sendToServerAction(metadata: INGXLoggerMetadata | INGXLoggerMetadata[], config: INGXLoggerConfig): void {
        let requestBody: any;

    const secureMetadata = (pMetadata: INGXLoggerMetadata) => {
        // Copying metadata locally because we don't want to change the object for the caller
        const securedMetadata: INGXLoggerMetadata = { ...pMetadata };
        securedMetadata.additional = this.secureAdditionalParameters(securedMetadata.additional ?? []);
            securedMetadata.message = this.secureMessage(securedMetadata.message);
            return securedMetadata;
        }

        if (Array.isArray(metadata)) {
            requestBody = [];
            metadata.forEach(m => {
                requestBody.push(secureMetadata(m));
            })
        } else {
            requestBody = secureMetadata(metadata);
        }

        // Allow users to customise the data sent to the API
        requestBody = this.customiseRequestBody(requestBody);

        const headers = config.customHttpHeaders || new HttpHeaders();
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }

        const logOnServerAction = () => {
            this.logOnServer(
                config.serverLoggingUrl ?? '',
                requestBody,
                {
                    headers,
                    params: config.customHttpParams || new HttpParams(),
                    responseType: config.httpResponseType || 'json',
                    withCredentials: config.withCredentials || false,
                },
            ).pipe(catchError(err => {
                // Do not use NGXLogger here because this could cause an infinite loop 
                console.error('NGXLogger: Failed to log on server', err);
                
                return of(null);
            })).subscribe();
        };

        if (config.serverCallsOutsideNgZone === true) {
            if (!this.ngZone) {
                console.error('NGXLogger: NgZone is not provided and serverCallsOutsideNgZone is set to true');
                return;
            }
            this.ngZone.runOutsideAngular(logOnServerAction);
        } else {
            logOnServerAction();
        }
    }
  
}